import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useRecordContext, useNotify, useRefresh } from 'react-admin';
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import BarChartIcon from '@mui/icons-material/BarChart';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ApiError } from '@rekindle/shared/api/errors';
import { manualAccessEnd, type AdminPlanGrant, type AdminSubscription, type ManualAccessDuration } from '@rekindle/shared/types/adminSubscription';
import { adminApi } from '../../providers/dataProvider';
import { PLAN_TIERS, PLAN_TIER_LABELS, PLAN_TIER_CHIP_COLORS } from '../../constants';
import { SubscriptionSource, SubscriptionAccess, useSubscriptionText } from './SubscriptionFields';
import type { AdminUserAccess } from '@rekindle/shared/types/adminSubscription';
import { PartnerLink } from '../users/UserAccessFields';

interface UserRecord extends Partial<AdminUserAccess> { id: string; planTier?: string; subscription?: AdminSubscription }

function ManualAccessDialog({ record, onClose }: { record: UserRecord; onClose: () => void }) {
  const t = useSubscriptionText(), refresh = useRefresh(), notify = useNotify();
  const [tier, setTier] = useState<'ember' | 'flame' | 'blaze'>(record.planTier === 'blaze' ? 'blaze' : 'flame');
  const [period, setPeriod] = useState(record.subscription?.source === 'manual'
    ? record.subscription.isLifetime ? 'lifetime' : '' : '1');
  const [customMonths, setCustomMonths] = useState('6');
  const [startsAt] = useState(() => new Date());
  const months = Number(period === 'custom' ? customMonths : period);
  const valid = tier === 'ember' || period === 'lifetime' || Number.isInteger(months) && months >= 1 && months <= 120;
  const duration: ManualAccessDuration = period === 'lifetime' ? { kind: 'lifetime' } : { kind: 'months', months };
  const endsAt = valid && tier !== 'ember' ? manualAccessEnd(startsAt, duration) : null;
  const save = useMutation({
    mutationFn: (grant: AdminPlanGrant) => adminApi.updateUserPlan(record.id, grant),
    retry: false,
    onSuccess: () => { refresh(); notify(t('saved', 'Offered access updated'), { type: 'success' }); onClose(); },
    onError: error => { if (error instanceof ApiError && error.statusCode === 409) refresh(); },
  });
  const blocked = record.subscription?.canManageManually !== true;
  const conflict = save.error instanceof ApiError && save.error.statusCode === 409;
  return <Dialog open onClose={() => { if (!save.isPending) onClose(); }} maxWidth="sm" fullWidth aria-labelledby="manual-access-title">
    <DialogTitle id="manual-access-title">{t('manage', 'Manage offered access')}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">{t('noCharge', 'This grants ReKindle benefits for free. No charge and no automatic renewal.')}</Typography>
        {save.isError && <Alert severity="error">{conflict
          ? t('conflict', 'This account can no longer be changed manually. Close this dialog and review its current subscription.')
          : t('saveFailed', 'Could not save access. Your selection is kept. Check the current subscription before trying again.')}</Alert>}
        <TextField id="manual-access-plan" select fullWidth label={t('plan', 'Plan')} value={tier} disabled={save.isPending || blocked}
          onChange={event => setTier(event.target.value as typeof tier)}>
          <MenuItem value="flame">Flame</MenuItem><MenuItem value="blaze">Blaze</MenuItem>
          <MenuItem value="ember">{t('remove', 'Ember — remove offered access')}</MenuItem>
        </TextField>
        {tier !== 'ember' && <>
          <TextField id="manual-access-duration" select fullWidth label={t('duration', 'Duration')} value={period} disabled={save.isPending || blocked}
            onChange={event => setPeriod(event.target.value)}>
            <MenuItem value="" disabled>{t('chooseDuration', 'Choose a duration')}</MenuItem>
            {[1, 3, 6, 12].map(count => <MenuItem key={count} value={String(count)}>{count === 1
              ? t('oneMonth', '1 month') : t('months', '%{count} months', { count })}</MenuItem>)}
            <MenuItem value="custom">{t('custom', 'Custom number of months')}</MenuItem>
            <MenuItem value="lifetime">{t('lifetime', 'Lifetime')}</MenuItem>
          </TextField>
          {period === 'custom' && <TextField id="manual-access-months" type="number" fullWidth label={t('monthCount', 'Number of months')}
            value={customMonths} disabled={save.isPending || blocked} onChange={event => setCustomMonths(event.target.value)}
            slotProps={{ htmlInput: { min: 1, max: 120, step: 1 } }} error={!valid}
            helperText={t('bounds', 'Between 1 and 120 months')} />}
          {valid && <Alert severity="info">{endsAt
            ? t('newEnd', 'Starts now. Access ends on %{date}.', { date: endsAt.toLocaleDateString(undefined, { dateStyle: 'long' }) })
            : t('noExpiry', 'Starts now with no expiration date. An admin can revoke this access later.')}</Alert>}
          {record.subscription?.source === 'manual' && <Typography variant="body2" color="text.secondary">
            {t('replaces', 'This replaces the current offered access. The selected duration starts today; it is not added to the existing end date.')}
          </Typography>}
        </>}
        {tier === 'ember' && <Alert severity="warning">{t('revokeWarning', 'Removes this user’s offered benefits immediately. Benefits shared by a partner are unaffected.')}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button disabled={save.isPending} onClick={onClose}>{t('cancel', 'Cancel')}</Button>
      <Button variant="contained" disabled={!valid || blocked || conflict || save.isPending}
        color={tier === 'ember' ? 'warning' : 'primary'}
        onClick={() => save.mutate(tier === 'ember' ? { planTier: 'ember' } : { planTier: tier, duration })}>
        {save.isPending ? t('saving', 'Saving…') : tier === 'ember' ? t('revoke', 'Remove offered access') : t('save', 'Save offered access')}
      </Button>
    </DialogActions>
  </Dialog>;
}

export function UserSubscriptionCard({ onGrantBonus }: { onGrantBonus: () => void }) {
  const record = useRecordContext<UserRecord>(), t = useSubscriptionText();
  const [open, setOpen] = useState(false);
  if (!record) return null;
  const subscription = record.subscription;
  const planTier = record.effectiveSubscription?.planTier ?? record.planTier ?? 'ember';
  const inherited = record.effectiveSubscription?.source === 'partner';
  return <Card data-testid="admin-user-subscription"><CardContent>
    <Stack spacing={2}>
      <Typography variant="h6">{t('title', 'Subscription')}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Chip label={PLAN_TIERS.find(plan => plan.value === planTier)?.label ?? planTier}
          color={PLAN_TIER_CHIP_COLORS[planTier as keyof typeof PLAN_TIER_CHIP_COLORS] ?? 'default'} />
        {inherited ? <Chip size="small" variant="outlined" label={t('fromPartner', 'From partner')} /> : <SubscriptionSource value={subscription} />}
      </Box>
      {inherited && <Box><PartnerLink partner={record.partner} /></Box>}
      <SubscriptionAccess value={record.effectiveSubscription?.access ?? subscription} />
      {inherited && <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">{t('personalPlan', 'Personal plan: %{plan}', { plan: PLAN_TIER_LABELS[record.planTier ?? 'ember'] })}</Typography>
        <SubscriptionSource value={subscription} />
      </Stack>}
      {subscription?.source === 'manual' && <Typography variant="body2" color="text.secondary">{t('noCharge', 'This grants ReKindle benefits for free. No charge and no automatic renewal.')}</Typography>}
      {subscription?.canManageManually !== true && <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />}>
        {!subscription ? t('missing', 'Subscription details are unavailable. Refresh before changing access.')
          : subscription.source === 'revenuecat' ? t('storeLocked', 'Managed by RevenueCat. Plan changes and renewal are handled in the store. Manual grants stay disabled after expiration.')
          : t('pending', 'A store purchase is awaiting verification. Resolve it before granting manual access.')}
      </Alert>}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<CardGiftcardIcon />} disabled={subscription?.canManageManually !== true}
          onClick={() => setOpen(true)}>{subscription?.source === 'manual' ? t('manage', 'Manage offered access') : t('grant', 'Offer access')}</Button>
        <Button variant="outlined" onClick={onGrantBonus}>{t('bonus', 'Grant bonus')}</Button>
        <Button variant="outlined" component={RouterLink} to={`/users/${record.id}/usage`} startIcon={<BarChartIcon />}>{t('usage', 'View usage')}</Button>
      </Box>
    </Stack>
    {open && <ManualAccessDialog key={record.id} record={record} onClose={() => setOpen(false)} />}
  </CardContent></Card>;
}
